export interface RequiredCheckSection {
  id: string;
  required: boolean;
  retired: boolean;
  type: string | null | undefined;
  content: unknown;
  value: unknown;
}

/**
 * The ids of the required sections "Mark as ready" would be refused for, in the order given.
 *
 * A copy of the server's submit check in workspace-api's briefs service (`isEmptySection` and
 * `isEmptyValue`). It must stay rule for rule the same: a section this calls filled but the
 * server calls empty sends the briefer back to the folder dialog and a toast, and one this calls
 * empty but the server accepts blocks a brief nobody can fix.
 */
export function findMissingRequiredSections(sections: readonly RequiredCheckSection[]): string[] {
  return sections.filter((s) => s.required && !s.retired && isEmptySection(s)).map((s) => s.id);
}

// Prose counts as an answer for a typed section too: an agent-generated brief arrives with its
// text in `content` and the template's type stamped on afterwards, with no `value`.
function isEmptySection({ type, content, value }: RequiredCheckSection): boolean {
  if (type == null || type === 'text') return !hasText(content);
  return isEmptyValue(type, value) && !hasText(content);
}

function hasText(node: unknown): boolean {
  if (!node || typeof node !== 'object') return false;
  const n = node as Record<string, unknown>;
  if (typeof n.text === 'string' && n.text.trim().length > 0) return true;
  return Array.isArray(n.content) && n.content.some(hasText);
}

function isEmptyValue(type: string, value: unknown): boolean {
  if (value == null) return true;
  const v = (typeof value === 'object' ? value : {}) as Record<string, unknown>;
  switch (type) {
    case 'date':
      return v.start == null && v.end == null;
    case 'select':
    case 'checklist':
      return !Array.isArray(v.selected) || v.selected.length === 0;
    case 'url':
      return v.url == null || v.url === '';
    case 'file':
      return !Array.isArray(v.assetIds) || v.assetIds.length === 0;
    case 'boolean':
      return v.value == null;
    default:
      // The server counts any value of a type it does not know yet as an answer.
      return false;
  }
}
